"use client";

import BlokWrap from "@/components/bloks/BlokWrap";
import { useGeo } from "@/i18n/routing";
import type { BlokRightOfWithdrawal } from "@/storyblok";
import type { BlokProps } from "@trydig/storyblok";
import { useWithdrawal, Withdrawal } from "@trydig/withdrawal/react";
import { declareWithdrawalAction, lookupOrderAction } from "./actions";
import styles from "./RightOfWithdrawalBlok.module.css";

const ErrorBox = ({ blok }: { blok: BlokRightOfWithdrawal }) => {
  const { phase } = useWithdrawal();

  return (
    <>
      {phase === "not_found" && (
        <p className={styles.error}>{blok.errorNotFound}</p>
      )}
      {phase === "expired" && (
        <p className={styles.error}>{blok.errorExpired}</p>
      )}
      {phase === "error" && <p className={styles.error}>{blok.error}</p>}
    </>
  );
};

const WithdrawalSummary = ({ blok }: { blok: BlokRightOfWithdrawal }) => {
  const { order, declared, email } = useWithdrawal();
  if (!order) return null;

  return (
    <>
      {declared ? (
        <Withdrawal.Success>
          <div className={styles.success}>
            <p className={styles.successTitle}>{blok.successHeader}</p>
            <p className={styles.successText}>{blok.successText}</p>
            <dl className={styles.successMeta}>
              <div>
                <dt>{blok.orderNumberLabel}</dt>
                <dd>#{order.orderNumber}</dd>
              </div>
              <div>
                <dt>{blok.confirmButton}</dt>
                <dd>{email}</dd>
              </div>
            </dl>
          </div>
        </Withdrawal.Success>
      ) : (
        <div className={styles.summary}>
          <Withdrawal.Items />

          {order.items.some(i => (i.claimed ?? 0) > 0) && (
            <div className={styles.success}>
              <p className={styles.successText}>{blok.withdrawedItemsText}</p>
            </div>
          )}

          <div className={styles.success}>
            <p className={styles.successText}>{blok.confirmationText} {email}</p>
          </div>

          <Withdrawal.ConfirmButton>
            {blok.confirmButton}
          </Withdrawal.ConfirmButton>
        </div>
      )}
    </>
  );
};

export const RightOfWithdrawalBlok = ({
  blok,
  ...rest
}: BlokProps<BlokRightOfWithdrawal>) => {
  const { geo } = useGeo();

  return (
    <BlokWrap editable={blok} className={styles.container} {...rest}>
      <Withdrawal.Root
        actions={{
          lookupOrder: lookupOrderAction,
          declareWithdrawal: declareWithdrawalAction,
        }}
        locale={geo}
      >
        <div className={styles.root}>
          <h2>{blok.header}</h2>

          <Withdrawal.Lookup>
            <div className={styles.form}>
              <Withdrawal.Field name="email" label={blok.emailLabel} />
              <Withdrawal.Field
                name="orderNumber"
                label={blok.orderNumberLabel}
              />

              <ErrorBox blok={blok} />

              <Withdrawal.LookupButton>
                {blok.continueButton}
              </Withdrawal.LookupButton>
            </div>
          </Withdrawal.Lookup>

          <WithdrawalSummary blok={blok} />
        </div>
      </Withdrawal.Root>
    </BlokWrap>
  );
};
